import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  Tabs,
  Tab,
  Box,
  Avatar,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import "./Profile.less";
import { getLoginData } from "../../../../redux/features/auth/authSlice";
import SecuritySettings from "./SecuritySettings";
import { useUpdateSingleUserMutation } from "../../../../redux/features/usersManagement/usersManagementApi";
import { useGetUserByUserIdQuery } from "../../../../redux/features/usersManagement/usersManagementApi";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage } from "react-intl";
import { DashboardButton } from "../../../common/DashboardButton";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { SkeletonLoading } from "../../../common/SkeletonLoading";
import { ErrorLoading } from "../../../common/ErrorLoading";
import ColumnContainer from "../../../common/ColumnContainer";

const Profile = () => {
  const { auth } = useAuth();
  const dispatch = useDispatch();

  const {
    data: userData,
    isLoading,
    isError,
  } = useGetUserByUserIdQuery(auth?.userData?.id, {
    skip: !auth?.userData?.id,
  });

  const [updateUser, { isLoading: isUpdateLoading }] =
    useUpdateSingleUserMutation();
  const [tabValue, setTabValue] = useState(0);
  const [avatar, setAvatar] = useState("");

  useEffect(() => {
    if (userData) {
      dispatch(getLoginData({ ...auth, userData: userData }));

      formik.setValues({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        company_name: userData.company_name || "",
        address: userData.address || "",
        city: userData.city || "",
        zip_code: userData.zip_code || "",
        country: userData.country || "",
        vat_number: userData.vat_number || "",
        phone: userData.phone || "",
      });

      const storedAvatar = localStorage.getItem("userAvatar");
      if (storedAvatar) {
        setAvatar(storedAvatar);
      }
    }
  }, [userData]);

  function handleTabChange(event, newValue) {
    setTabValue(newValue);
  }

  const handleSave = async (values) => {
    try {
      const userId = auth?.userData?.id;
      await updateUser({ userId, ...values });
    } catch (error) {
      console.error("Errore nel salvataggio dei dati:", error);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setAvatar(reader.result);
      localStorage.setItem("userAvatar", reader.result);
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, <FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    last_name: Yup.string().min(
      2,
      <FormattedMessage id="register.form.warnings.field" />
    ),
    email: Yup.string().email(
      <FormattedMessage id="register.form.warnings.field" />
    ),
    company_name: Yup.string().required(
      <FormattedMessage id="register.form.warnings.empty" />
    ),
    address: Yup.string().required(
      <FormattedMessage id="register.form.warnings.empty" />
    ),
    city: Yup.string().min(
      2,
      <FormattedMessage id="register.form.warnings.field" />
    ),
    zip_code: Yup.number()
      .typeError(<FormattedMessage id="register.form.warnings.number.type" />)
      .test(
        "len",
        <FormattedMessage id="register.form.warnings.field" />,
        (val) => val && val.toString().length === 5
      ),
    vat_number: Yup.string().min(
      11,
      <FormattedMessage id="register.form.warnings.field" />
    ),
    phone: Yup.number()
      .typeError(<FormattedMessage id="register.form.warnings.number.type" />)
      .test(
        "len",
        <FormattedMessage id="register.form.warnings.field" />,
        (val) => val && val.toString().length <= 13
      ),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      company_name: "",
      address: "",
      city: "",
      zip_code: "",
      country: "",
      vat_number: "",
      phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSave,
  });

  const fields = [
    {
      label: <FormattedMessage id="register.form.firstname" />,
      type: "text",
      name: "first_name",
      placeholder: <FormattedMessage id="register.form.firstname" />,
    },
    {
      label: <FormattedMessage id="register.form.lastname" />,
      type: "text",
      name: "last_name",
      placeholder: <FormattedMessage id="register.form.lastname" />,
    },
    {
      label: <FormattedMessage id="register.form.email" />,
      type: "email",
      name: "email",
      placeholder: <FormattedMessage id="register.form.email" />,
    },
    {
      label: <FormattedMessage id="register.form.company" />,
      type: "text",
      name: "company_name",
      placeholder: <FormattedMessage id="register.form.company" />,
    },
    {
      label: <FormattedMessage id="register.form.address" />,
      type: "text",
      name: "address",
      placeholder: <FormattedMessage id="register.form.address" />,
    },
    {
      label: <FormattedMessage id="register.form.city" />,
      type: "text",
      name: "city",
      placeholder: <FormattedMessage id="register.form.city" />,
    },
    {
      label: <FormattedMessage id="register.form.zip" />,
      type: "text",
      name: "zip_code",
      placeholder: <FormattedMessage id="register.form.zip" />,
    },
    {
      label: <FormattedMessage id="register.form.country" />,
      type: "text",
      name: "country",
      placeholder: <FormattedMessage id="register.form.country" />,
    },
    {
      label: <FormattedMessage id="register.form.vat" />,
      type: "text",
      name: "vat_number",
      placeholder: <FormattedMessage id="register.form.vat" />,
    },
    {
      label: <FormattedMessage id="register.form.phone" />,
      type: "text",
      name: "phone",
      placeholder: <FormattedMessage id="register.form.phone" />,
    },
  ];

  return (
    <Box className="profile-edit-container">
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        TabIndicatorProps={{ style: { backgroundColor: "#f66f0d" } }}
      >
        <Tab
          label={<FormattedMessage id="profile.tab1" />}
          className="MuiTab-root"
        />
        <Tab
          label={<FormattedMessage id="profile.tab2" />}
          className="MuiTab-root"
        />
      </Tabs>

      {isLoading ? (
        <SkeletonLoading type="profile" />
      ) : isError ? (
        <ColumnContainer padding="10px">
          <ErrorLoading />
        </ColumnContainer>
      ) : userData && tabValue === 0 ? (
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={4} md={3}>
            <div className="avatar-container">
              <Avatar
                alt={`${userData?.first_name} ${userData?.last_name}`}
                src={avatar || "https://i.pravatar.cc/150?u=2"}
                className="avatar-large"
              />
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <IconButton component="span" className="edit-icon">
                  <EditIcon
                    fontSize="small"
                    sx={{ "& path": { color: "var(--text-primary)" } }}
                  />
                </IconButton>
              </label>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={9}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                {fields.map((field, index) => (
                  <Grid item xs={12} sm={12} md={6} key={index}>
                    <Typography
                      variant="p"
                      component="p"
                      className="input-label"
                    >
                      {field.label}
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      name={field.name}
                      value={formik.values[field.name]}
                      type={field.type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched[field.name] &&
                        Boolean(formik.errors[field.name])
                      }
                      helperText={
                        formik.touched[field.name] && formik.errors[field.name]
                      }
                      className="input-field"
                    />
                  </Grid>
                ))}
              </Grid>

              <Grid xs={12} container justifyContent="flex-end" paddingTop={4}>
                <DashboardButton
                  type="filled"
                  disabled={isLoading || !formik.isValid}
                >
                  <FormattedMessage id="button.save" />
                </DashboardButton>
              </Grid>
            </form>
          </Grid>
        </Grid>
      ) : tabValue === 1 ? (
        <SecuritySettings />
      ) : null}
    </Box>
  );
};

export default Profile;
